/**
 * A table's LEAVES are strings, arrays and functions; everything else is a group. The same shape as the app's own
 * tables (`apps/mobile/src/copy/copyKeys.ts`), so the two can be read the same way.
 */
type Leaf = string | readonly unknown[] | ((...args: never[]) => unknown);

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends Leaf ? T[K] : DeepPartial<T[K]>;
};

const isGroup = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Every leaf's dotted key, in table order. */
export function leafPaths(table: unknown, prefix = ''): string[] {
  if (!isGroup(table)) return prefix ? [prefix] : [];
  return Object.keys(table).flatMap((k) => leafPaths(table[k], prefix ? `${prefix}.${k}` : k));
}

/** The leaf at a dotted key, or undefined. */
export function valueAt(table: unknown, path: string): unknown {
  let at: unknown = table;
  for (const k of path.split('.')) {
    if (!isGroup(at)) return undefined;
    at = at[k];
  }
  return at;
}

/** The keys `source` has and `other` lacks. */
export function missingKeys(source: unknown, other: unknown): string[] {
  return leafPaths(source).filter((p) => valueAt(other, p) === undefined);
}

/** `over` laid over `base`, leaf by leaf: a key `over` lacks keeps `base`'s. */
export function overlay<T>(base: T, over: DeepPartial<T> | undefined): T {
  return overlayAny(base, over) as T;
}

function overlayAny(base: unknown, over: unknown): unknown {
  if (!isGroup(base) || !isGroup(over)) return over ?? base;
  const out: Record<string, unknown> = { ...base };
  for (const k of Object.keys(over)) out[k] = overlayAny(base[k], over[k]);
  return out;
}
